import { plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';
import { fieldsMatchQuery } from './fuzzyMatch.ts';
import { stampedParticipantLabel } from './threadLabels.ts';

export interface SentThreadLike {
  kind: 'dm' | 'group';
  title?: string | null;
  participantUids: string[];
  participantLabels?: Record<string, string>;
}

export interface SentMessageLike {
  text?: string | null;
  recalled?: boolean;
  attachments?: unknown[];
}

export interface SentThreadVersion {
  id: string;
  version: number;
}

export const MAX_CONCURRENT_SENT_READS = 6;

/**
 * Return only threads whose current version is neither loaded, queued, nor
 * already in flight. This keeps an inbox that arrives one snapshot at a time
 * to one read per thread instead of repeatedly re-reading the whole prefix.
 */
export function sentThreadReadPlan(
  current: readonly SentThreadVersion[],
  loaded: ReadonlyMap<string, number>,
  queued: ReadonlyMap<string, number>,
  inFlight: ReadonlyMap<string, number>
): SentThreadVersion[] {
  return current.filter(({ id, version }) =>
    loaded.get(id) !== version &&
    queued.get(id) !== version &&
    !inFlight.has(id)
  );
}

export function sentThreadLabel(
  thread: SentThreadLike,
  meUid: string,
  labelForUid: (uid: string) => string,
  /**
   * Packet J32 finding 2 (Fable's refute round on PR #1141): a merged DM
   * thread's participantUids can hold BOTH of the sender's own uids (and,
   * since finding 3, both of the recipient's too), so raw
   * `participantUids.filter(uid => uid !== meUid)` counts uids, not humans -
   * a real two-person DM read as "Ella Ford, Ella Ford (Group, 3 people)".
   * When the caller can resolve identity (jrStaffRoster.ts's
   * threadOtherHumanUids, which also removes the caller's OWN alias set,
   * not just her current uid), pass the already-collapsed, canonical-uid
   * list here. Omit it and this keeps today's exact uid-count behaviour,
   * unaffected.
   */
  otherHumanUids?: readonly string[]
): string {
  const participantUids = Array.from(new Set(thread.participantUids));
  const otherUids = otherHumanUids ?? participantUids.filter((uid) => uid !== meUid);
  const participantLabel = (uid: string) =>
    stampedParticipantLabel(thread.participantLabels, uid) ?? labelForUid(uid);

  // THIRD REFUTE ROUND, FINDING (P3), PROVEN BY EXECUTION: a family viewer
  // has no directory to collapse with (useUsersList hands a family account
  // only its own document), so a parent who DM'd Ella's email account could
  // never pass otherHumanUids at all - and the old `otherUids.length === 1`
  // test then fell to the GROUP label the moment the thread's row widened
  // to three uids, reading "(Group, 3 people)" for a real two-person DM.
  // `kind === 'dm'` is now a server-owned fact true by construction (rules'
  // create clause still forces a plain client dm to exactly 2 uids; a
  // merged dm is only ever written by dmForJrStaff.mjs's Admin SDK, which
  // never sets kind to anything else) - so the KIND decides the branch, not
  // a uid or human count. Pick the best name this caller can produce:
  // otherHumanUids[0] (canonical, when a directory resolved it) or plain
  // otherUids[0] (whichever uid sorts first in participantUids, when it did
  // not) - either beats the group label for what is always really one
  // other human.
  if (thread.kind === 'dm') {
    return participantLabel(otherUids[0] ?? '') || 'Conversation';
  }

  const safeTitle = typeof thread.title === 'string' ? thread.title.trim() : '';
  const groupLabel =
    safeTitle ||
    otherUids
      .map(participantLabel)
      .filter(Boolean)
      .slice(0, 3)
      .join(', ') ||
    'Group conversation';
  const peopleLabel = `${participantUids.length} ${participantUids.length === 1 ? 'person' : 'people'}`;
  return `${groupLabel} (Group, ${peopleLabel})`;
}

export function sentMessagePreview(message: SentMessageLike): string {
  if (message.recalled) return 'This message was recalled.';
  const text = plainMessageText(message.text).trim();
  if (text) return text;
  const attachmentCount = message.attachments?.length ?? 0;
  if (attachmentCount > 0) {
    return `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`;
  }
  return 'Message';
}

/**
 * FUZZY since 2026-09-15, through the one shared matcher every other search
 * box on the Messages page already uses (Bobbie: "Fuzzy matching please for
 * searches"). Until then this was the page's one exact-substring box: a
 * typo the inbox forgave, the Sent view refused. The page passes its own
 * PAST_SEARCH_THRESHOLD; the default here is the same number for callers
 * that have none.
 */
export function sentMessageMatchesSearch(
  recipientLabel: string,
  message: SentMessageLike,
  query: string,
  threshold = 0.5
): boolean {
  return fieldsMatchQuery([recipientLabel, sentMessagePreview(message)], query, threshold);
}
